import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense, useEffect } from 'react';
import { useRecoilValue } from 'recoil';

import type { OpenAPISpec } from '@gitbook/api-client';
import { UserTheme } from '@gitbook/app-database';
import type { NavigationScreenOpenAPISpecsHomeState } from '@gitbook/app-navigation';
import {
    DialogRoot,
    LoadingPanel,
    type OverlayTriggerState,
    Page,
    PageMain,
    useDialogTriggerState,
} from '@gitbook/spine-react';
import { HeadTitle } from '@gitbook/util-react-head';
import { assert } from '@gitbook/util-ts';

import { bgGradient } from '../../assets';
import {
    APIErrorBoundary,
    OpenAPIEmptyState,
    OpenAPISpecCreateDialog,
    OrganizationMissingPermRedirect,
} from '../../Components';
import {
    getLastSeenOpenAPIScreen,
    NavigationScreenWithOrganization,
    useNavigation,
    useSetAnalyticsScreen,
} from '../../navigation';
import { apiQuery, userThemeAtom } from '../../state';
import emptyStateBgImg from './empty-state-bg.svg';
import emptyStateBgDarkImg from './empty-state-bg-dark.svg';

/**
 * Home screen for OpenAPI specifications.
 */
export function OpenAPISpecsHomeScreen(props: { screen: NavigationScreenOpenAPISpecsHomeState }) {
    return (
        <NavigationScreenWithOrganization screen={props.screen}>
            {({ screen }) => (
                <APIErrorBoundary
                    fallback={{
                        403: (
                            <OrganizationMissingPermRedirect
                                title="Error while accessing OpenAPI specification"
                                organizationId={screen.organization}
                            />
                        ),
                    }}
                >
                    <HeadTitle title="OpenAPI specifications" />
                    <TrackAnalyticsScreen organizationId={screen.organization} />
                    <OpenAPISpecsHomeScreenWithOrganization screen={screen} />
                </APIErrorBoundary>
            )}
        </NavigationScreenWithOrganization>
    );
}

/**
 * Home screen for OpenAPI specifications with organization.
 */
function OpenAPISpecsHomeScreenWithOrganization(props: {
    screen: NavigationScreenOpenAPISpecsHomeState & { organization: string };
}) {
    const { screen } = props;
    const addOpenAPISpecTrigger = useDialogTriggerState();
    const { navigate } = useNavigation();

    return (
        <Page>
            <PageMain className="h-screen justify-center">
                <Suspense fallback={<LoadingPanel size="large" />}>
                    <OpenAPISpecsHome
                        organizationId={screen.organization}
                        addOpenAPISpecTrigger={addOpenAPISpecTrigger}
                    />
                </Suspense>
                <DialogRoot {...addOpenAPISpecTrigger.rootProps}>
                    <OpenAPISpecCreateDialog
                        organizationId={screen.organization}
                        onCreated={(openAPISpec) => {
                            navigate({
                                screen: {
                                    type: 'openapi_spec',
                                    organization: screen.organization,
                                    spec: openAPISpec.slug,
                                },
                            });
                        }}
                        analyticsContext={{
                            triggerLocation: 'home-empty-state-action',
                        }}
                    />
                </DialogRoot>
            </PageMain>
        </Page>
    );
}

/**
 * Track a page view on the current screen.
 */
function TrackAnalyticsScreen(props: { organizationId: string }) {
    useSetAnalyticsScreen(
        {
            organization: props.organizationId,
            screen: 'openapi_specs',
        },
        [props.organizationId]
    );
    return null;
}

/**
 * Fetch and list OpenAPI specs.
 */
function OpenAPISpecsHome(props: {
    organizationId: string;
    addOpenAPISpecTrigger: OverlayTriggerState;
}) {
    const userTheme = useRecoilValue(userThemeAtom);
    const { organizationId, addOpenAPISpecTrigger } = props;
    const { data } = useSuspenseQuery(apiQuery('listOpenAPISpecs', { organizationId, limit: 100 }));

    if (data.items.length === 0) {
        return (
            <div>
                <div
                    className="absolute inset-0"
                    style={{
                        background: `center / cover no-repeat url(${bgGradient.light})`,
                    }}
                />
                <div
                    className="absolute inset-0 hidden md:block"
                    style={{
                        background: `center no-repeat url(${userTheme === UserTheme.Dark ? emptyStateBgDarkImg : emptyStateBgImg})`,
                    }}
                />
                <OpenAPIEmptyState
                    organizationId={organizationId}
                    addOpenAPISpecTrigger={addOpenAPISpecTrigger}
                    className="relative bg-base shadow-elevated"
                    analyticsContext={{
                        triggerLocation: 'home-empty-state-action',
                    }}
                />
            </div>
        );
    }

    return (
        <RedirectToLatestOpenAPISpec organizationId={organizationId} openAPISpecs={data.items} />
    );
}

/**
 * Redirect to the latest visited OpenAPI spec.
 */
function RedirectToLatestOpenAPISpec(props: {
    openAPISpecs: OpenAPISpec[];
    organizationId: string;
}) {
    const { openAPISpecs, organizationId } = props;
    const lastSeenScreen = getLastSeenOpenAPIScreen();
    const lastSeenSpec = lastSeenScreen
        ? openAPISpecs.find((spec) => spec.slug === lastSeenScreen.spec)
        : null;
    const openAPISpec = lastSeenSpec ?? openAPISpecs[0];
    assert(openAPISpec, 'No OpenAPI spec found');
    const { navigate } = useNavigation();
    useEffect(() => {
        navigate(
            {
                screen: {
                    type: 'openapi_spec',
                    organization: organizationId,
                    spec: openAPISpec.slug,
                },
            },
            { replace: true }
        );
    }, [openAPISpec, navigate, organizationId]);
    return null;
}
